import { Row, Col, Space } from 'antd';
import { AccountingYearPicker } from './AccountingYearPicker';
import { IncomeStatementCard } from './IncomeStatementCard';
import { TopPayersCard } from './TopPayersCard';
import { TopVendorsCard } from './TopVendorsCard';
import { UncategorizedTransactionsBanner } from './UncategorizedTransactionsBanner';
import { ReportPackSection } from './ReportPackSection';
import { ExpenseBreakdownPie } from './ExpenseBreakdownPie';
import { AccountingDisclaimer } from './shared/AccountingDisclaimer';
import { IncompleteProfileState } from './IncompleteProfileState';
import { useIncomeStatement } from './hooks/useIncomeStatement';
import { useAccountingYear } from './hooks/useAccountingYear';
import { DemoDataAlert } from '../../home/components/DemoDataAlert';
import { DemoDataWrapper } from '../../home/components/DemoDataBadge';
import { useIsPreActiveCustomer } from '../../../../../../hooks/useIsPreActiveCustomer';

interface AccountingHomeProps {
  customerId: string;
  businessType: string | null | undefined;
  earliestYear: number;
}

function AccountingContent({
  customerId,
  year,
  earliestYear,
}: {
  customerId: string;
  year: number;
  earliestYear: number;
}) {
  // Income statement is fetched here so we can pass uncategorized counts to
  // the banner and the generate modal without re-fetching.
  const { data: income } = useIncomeStatement(customerId, year);
  const isPreActive = useIsPreActiveCustomer();

  return (
    <div className="flex h-full w-full max-w-[1148px] flex-col space-y-[16px] overflow-y-auto overflow-x-hidden pb-4 pt-[16px]">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {isPreActive && <DemoDataAlert />}

        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <AccountingYearPicker earliestYear={earliestYear} />
        </Space>

        {income && income.uncategorized_count > 0 && (
          <UncategorizedTransactionsBanner
            count={income.uncategorized_count}
            totalCents={income.uncategorized_total}
          />
        )}

        {/* Each card carries its own "Preview Data" hover ribbon, the same as
            the widgets on Home, Banking and Monthly Budget. The banner above
            states it once; the ribbons keep it attached to the figures. */}
        <DemoDataWrapper showBadge={isPreActive}>
          <IncomeStatementCard customerId={customerId} year={year} />
        </DemoDataWrapper>

        <DemoDataWrapper showBadge={isPreActive}>
          <ExpenseBreakdownPie byCategory={income?.by_category} year={year} />
        </DemoDataWrapper>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <DemoDataWrapper showBadge={isPreActive}>
              <TopPayersCard customerId={customerId} year={year} />
            </DemoDataWrapper>
          </Col>
          <Col xs={24} md={12}>
            <DemoDataWrapper showBadge={isPreActive}>
              <TopVendorsCard customerId={customerId} year={year} />
            </DemoDataWrapper>
          </Col>
        </Row>

        <DemoDataWrapper showBadge={isPreActive}>
          <ReportPackSection
            customerId={customerId}
            year={year}
            uncategorizedCount={income?.uncategorized_count ?? 0}
            uncategorizedTotalCents={income?.uncategorized_total ?? 0}
          />
        </DemoDataWrapper>

        <AccountingDisclaimer isPreActive={isPreActive} />
      </Space>
    </div>
  );
}

export function AccountingHome({
  customerId,
  businessType,
  earliestYear,
}: AccountingHomeProps) {
  const isPreActive = useIsPreActiveCustomer();
  const year = useAccountingYear(earliestYear);

  // Pre-active customers skip this gate. The demo statement doesn't depend on
  // the business profile, and the CTA here points at Account Settings — which
  // isn't their next step. Theirs is finishing onboarding.
  if (!businessType && !isPreActive) return <IncompleteProfileState />;

  return (
    <AccountingContent
      customerId={customerId}
      year={year}
      earliestYear={earliestYear}
    />
  );
}
